import * as React from 'react';
import { flow, forEach, get, head, map } from 'lodash';
import { Field, formValueSelector, getFormValues, InjectedFormProps, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { Button, Col, Form, Grid, Row } from 'react-bootstrap';
import { IPolicy, IPostcodeAddress, ITraveller } from '../../../../../../../data/api/models';
import FieldFormControl from '../../../../../../../components/FieldFormControl';
import {
  allowOnlyApostrophes,
  hasEmojis, hasSpecialCharacters,
  isNotLongerThan,
  isPhone,
  isPostcode,
  isRequired
} from '../../../../../../../util/validators';
import { IStore } from '../../../../../../../redux/IStore';
import PostcodeControl from '../../../../../../../components/PostcodeControl';
import { IMtaFormNames } from '../../../../../../../services/mta/models';
import { ICustomerMTAOptions } from '../../../index';
import { MTAButtons } from '../../MTAButtons';
import '../style.scss';
import { differenceInHours } from 'date-fns';
import { Link } from 'react-router-dom';

interface IPersonalDetailsProps extends InjectedFormProps {
  activePolicies: IPolicy[];
  customerMTAOptions: ICustomerMTAOptions;
  formValues: any;
  dispatch: any;
  travellers: ITraveller[];
}

interface IPersonalDetailsState {
  travellers?: ITraveller[];
  policy?: IPolicy;
  showAddress?: boolean;
}

export const TITLES = ['Mrs', 'Miss', 'Mr', 'Ms', 'Mx', 'Dr']

class PersonalDetailsForm extends React.Component<IPersonalDetailsProps, IPersonalDetailsState> {

  public state = {
    travellers: [],
    policy: null,
  };

  constructor(props: IPersonalDetailsProps) {
    super(props);
    this.setTravellers();
    this.patchFormValues();
  }

  private getSelectedPolicy = (): IPolicy => {
    const { activePolicies } = this.props;
    let selectedPolicy: IPolicy = null;
    forEach(activePolicies, (policySelected) => {
      if (location.pathname.match(policySelected.id)) {
        selectedPolicy = policySelected;
      }
    });

    return selectedPolicy;
  }

  private setTravellers = (): void => {
    const { formValues } = this.props;
    let travellers: ITraveller[];
    const selectedPolicy: IPolicy = this.getSelectedPolicy();

    // Keeping the changes if we come back from the summary page
    travellers = (formValues && formValues.travellers && formValues.travellers.length) ?
      formValues.travellers :
      selectedPolicy.travellers;

    this.state = {
      travellers,
      policy: selectedPolicy,
    };

  }

  private isLeadTravellerNameEditable = (): boolean => {
    const { customerMTAOptions } = this.props;
    if (customerMTAOptions.customer_name.enabled && this.state.policy.issuedAt) {
      const difference = differenceInHours(new Date(), this.state.policy.issuedAt);
      return difference <= customerMTAOptions.customer_name.duration_in_hours_from_issue;
    }
    return false;
  }

  private isTravellerNameEditable = (): boolean => {
    const { customerMTAOptions } = this.props;
    if (customerMTAOptions.travellers_names.enabled && this.state.policy.issuedAt) {
      const difference = differenceInHours(new Date(), this.state.policy.issuedAt);
      return difference <= customerMTAOptions.travellers_names.duration_in_hours_from_issue;
    }
    return false;
  }

  private patchFormValues = (): void => {
    const { dispatch, change, reset } = this.props;
    const travellers = this.state.travellers;
    dispatch(reset());

    if (travellers && travellers.length) {
      dispatch(change('policyId', this.state.policy.id));

      travellers.forEach((traveller, index) => {
        dispatch(change(`travellers[${index}].type`, traveller.type));
        dispatch(change(`travellers[${index}].title`, traveller.title));
        dispatch(change(`travellers[${index}].firstName`, traveller.firstName));
        dispatch(change(`travellers[${index}].lastName`, traveller.lastName));
      });

      const postcode = get(travellers[0], 'address.postcode', '');
      const line1 = get(travellers[0], 'address.line1', '');
      const line2 = get(travellers[0], 'address.line2', '');
      const line3 = get(travellers[0], 'address.line3', '');
      const town = get(travellers[0], 'address.town', '');
      const county = get(travellers[0], 'address.county', '');

      dispatch(change('travellers[0].email', travellers[0].email));
      dispatch(change('travellers[0].address.postcode', postcode));
      dispatch(change('travellers[0].address.line1', line1));
      dispatch(change('travellers[0].address.line2', line2));
      dispatch(change('travellers[0].address.line3', line3));
      dispatch(change('travellers[0].address.town', town));
      dispatch(change('travellers[0].address.county', county));
      dispatch(change('travellers[0].phone', travellers[0].phone));
    }
  }

  private renderTravellerDetails = () => {
    const travellers: ITraveller[] = this.state.travellers;
    let detailsRow;
    if (travellers && travellers.length) {
      return (
        travellers.map((traveller: ITraveller, index) => {
          if (index === 0 && this.isLeadTravellerNameEditable()) {
            detailsRow = this.renderTravellerDetailsRow(index, true);
          } else if (index === 0 && !this.isLeadTravellerNameEditable()) {
            detailsRow = this.renderNoOptionToChangeContent('Lead Traveller Details');
          }
          if (index > 0 && this.isTravellerNameEditable()) {
            detailsRow = this.renderTravellerDetailsRow(index, true);
          } else if (index > 0 && !this.isTravellerNameEditable()) {
            detailsRow = null;
          }
          return (
            <>
              {detailsRow}
            </>
          );
        }));
    }
  }

  private renderNoOptionToChangeContent = (title: string) => {
    return (
      <div className="card-panel">
        <div className={'card-panel__title'}>
          <strong>
            {title}
          </strong>
        </div>
        <div className={'card-panel__field'}>
          Sorry You can’t make this change to your policy through the Portal yet.
          To get in contact please click <a className="pointer" href={'https://www.admiral.com/contact-us/travel'}
                                            target={'_blank'}>here</a>.
        </div>
      </div>
    );
  }

  private renderTravellerDetailsRow = (index: number, editable: boolean) => {
    const { travellers } = this.props;
    const travellersState = this.state.travellers;
    const leadTraveller = head(travellers);
    if (editable) {
      return (
        <div key={index}>
          <Row className={'dynamic-row'}>
            <Col xs={6} className={'full-width-xs'}><strong>Your preferred title</strong></Col>
            <Col xs={6} className={'full-width-xs title-selection-container'}>
              {map(TITLES, (value, key) => {
                const isSelected = leadTraveller?.title === value;
                return (
                  <Col xs={4}
                       className={isSelected ? 'traveller-title-selection selected' : 'traveller-title-selection'}>
                    <div>
                      <Field
                        name={`travellers[${index}].title`}
                        id={key + '-btn'}
                        type="radio"
                        value={value}
                        component={FieldFormControl}
                        validate={[isRequired]}
                      >
                        <div className={`trip-type-btn ${key}`}>
                          <div className={'trip-type-selection-container'}>
                            {value}
                          </div>
                        </div>
                      </Field>
                    </div>
                  </Col>
                )
              })}
            </Col>
          </Row>
          <div className="card-panel">
            <div className="card-panel__title">
              <strong>
                {'First Name'}
              </strong>
            </div>
            <div className={'card-panel__field'}>
              <Field
                name={`travellers[${index}].firstName`}
                type="text"
                placeholder="First Name"
                className="form-field personal-details"
                component={FieldFormControl}
                validate={[isRequired, hasEmojis, hasSpecialCharacters]}
              />
            </div>
          </div>
          <div className="card-panel">
            <div className="card-panel__title">
              <strong>
                {'Last Name'}
              </strong>
            </div>
            <div className={'card-panel__field'}>
              <Field
                name={`travellers[${index}].lastName`}
                type="text"
                placeholder="Last Name"
                className="form-field personal-details"
                component={FieldFormControl}
                validate={[isRequired, hasEmojis, allowOnlyApostrophes]}
              />
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div key={index}>
          <Row className="card-panel">
            <Col sm={3}>
              <strong>
                {index === 0 ? 'Lead Traveller Details' : `Traveller ${index + 1} Details`}
              </strong>
            </Col>
            <Col sm={3} md={2}>{travellersState[index].title}</Col>
            <Col sm={3}>{travellersState[index].firstName}</Col>
            <Col sm={3} md={4}>{travellersState[index].lastName}</Col>
          </Row>
        </div>
      );
    }
  }

  private handleShowAddress = () => {
    this.setState({ showAddress: true });
  }

  private renderTravellerPostcode = () => {
    const { customerMTAOptions } = this.props;
    const postcodeValidators = [
      isRequired,
      isPostcode,
      hasEmojis,
      isNotLongerThan(8)
    ];

    let detailsRow;

    if (customerMTAOptions.customer_address.enabled) {
      detailsRow = (
        <>
          <div className="card-panel wrap">
            <div className="card-panel__title">
              <strong>Home postcode</strong>
            </div>
            <div className={'card-panel__field'}>
              <div className="post-code-selector">
                <Field
                  autoComplete="new-password"
                  name={`travellers[0].address.postcode`}
                  type="text"
                  className="form-field personal-details"
                  component={PostcodeControl}
                  showManualEntry={false}
                  clearButton={true}
                  onClearAddress={this.handleClearAddress}
                  onSelectAddress={this.handleSelectAddress}
                  onManualEntry={this.handleShowAddress}
                  validate={postcodeValidators}
                />
              </div>
            </div>
          </div>
          <div className="card-panel">
            <div className="card-panel__title">
              <strong>Traveller Address</strong>
            </div>

            <div className={'card-panel__field'}>
              <div>
                <Field
                  name={`travellers[0].address.line1`}
                  type="text"
                  placeholder="Line 1"
                  className="form-field personal-details"
                  component={FieldFormControl}
                  onSelectAddress={this.handleSelectAddress}
                  validate={[
                    isRequired,
                    hasEmojis,
                  ]}
                />
                <Field
                  autoComplete="new-password"
                  className="form-field mt-10 personal-details"
                  name="travellers[0].address.line2"
                  placeholder="Line 2"
                  type="text"
                  component={FieldFormControl}
                  validate={[
                    hasEmojis,
                  ]}
                />
                <Field
                  autoComplete="new-password"
                  className="form-field mt-10 personal-details"
                  name="travellers[0].address.line3"
                  placeholder="Line 3"
                  type="text"
                  component={FieldFormControl}
                  validate={[
                    hasEmojis,
                  ]}
                />
                <Field
                  autoComplete="new-password"
                  className="form-field mt-10 personal-details"
                  name="travellers[0].address.town"
                  type="text"
                  placeholder="Town"
                  component={FieldFormControl}
                  validate={[
                    hasEmojis,
                  ]}
                />
                <Field
                  autoComplete="new-password"
                  className="form-field mt-10 personal-details"
                  name="travellers[0].address.county"
                  placeholder="County"
                  type="text"
                  component={FieldFormControl}
                  validate={[
                    hasEmojis,
                  ]}
                />
              </div>
            </div>
          </div>
        </>

      );
    } else {
      detailsRow = this.renderNoOptionToChangeContent('Address');
    }

    return (
      <>
        {detailsRow}
      </>
    );
  }

  private handleClearAddress = () => {
    this.handleSelectAddress(null);
  }

  private handleSelectAddress = (address: IPostcodeAddress) => {
    const { change } = this.props;
    const base = 'travellers[0].address';

    change(`${base}.line1`, get(address, 'line1', ''));
    change(`${base}.line2`, get(address, 'line2', ''));
    change(`${base}.line3`, get(address, 'line3', ''));
    change(`${base}.town`, get(address, 'town', ''));
    change(`${base}.county`, get(address, 'county', ''));
    change(`${base}.postcode`, get(address, 'postcode', ''));
  }

  private renderTravellerPhone = () => {
    const { customerMTAOptions } = this.props;
    let detailsRow;

    if (customerMTAOptions.phone_number.enabled) {
      detailsRow = (
        <div className="card-panel">
          <div className="card-panel__title">
            <strong>Phone Number</strong>
          </div>
          <div className={'card-panel__field'}>
            <Field
              autoComplete="new-password"
              name={`travellers[0].phone`}
              type="text"
              className="form-field personal-details"
              placeholder="Phone"
              component={FieldFormControl}
              validate={[isRequired, isPhone]}
            />
          </div>
        </div>
      );
    } else {
      detailsRow = this.renderNoOptionToChangeContent('Telephone');
    }
    return (
      <>
        {detailsRow}
      </>
    );
  }

  public render() {
    const { handleSubmit, submitting, valid, submitFailed } = this.props;

    const submitButton = (
      <Button
        bsStyle="primary"
        type="submit"
        bsSize="small"
        className="medium-width pull-right secondary-button"
        disabled={submitting || (submitFailed && !valid)}
      >
        APPLY CHANGES
      </Button>
    );

    const goBackButton = (
      <Link to="/account/summary">
        <Button
          bsStyle={'primary'}
          className={'secondary-button'}>
          GO BACK
        </Button>
      </Link>
    );

    return (
      <div style={{ marginTop: '15px' }}>
        <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">

          <div className="admiral-card" style={{ maxWidth: '650px', margin: '0 auto' }}>
            <div className={'p-details-overview-header'}>
              <div className={'p-d-overview-icon'}>
                <img src={require('../../../Images/user.svg')} width={'50px'} alt={''}/>
              </div>
              <div>
                <div className={'p-d-overview-text'}>
                  Update your personal details
                </div>
                <div>Lead traveller and policy holder</div>
              </div>
            </div>
            <div className="body">
              <Grid fluid={true}>
                {/*Travellers*/}
                <div>
                  {this.renderTravellerDetails()}
                </div>
                {/*Phone*/}
                <div>
                  {this.renderTravellerPhone()}
                </div>
                {/*Postcode*/}
                <div>
                  {this.renderTravellerPostcode()}
                </div>
              </Grid>
            </div>
          </div>

          <div className="admiral-card" style={{ maxWidth: '650px', margin: '10px auto', padding: '15px' }}>
            <div className={'info-box-blue-container'}>
              <div className={'icon'}><img src={require('../../../Images/question.svg')} width={'50px'} alt={''}/></div>
              <div className={'text'}>
                <div>Looking to update medical information for the policy holder/lead traveller?</div>
                <div>Visit Travellers Details from the make a change menu.</div>
                <div>Want to update your email you'll need to <a href={'https://www.admiral.com/contact-us/travel'} target={'_blank'}>contact us</a>.</div>
              </div>
            </div>
          </div>

          <div style={{ maxWidth: '650px', margin: '10px auto' }}>
            <MTAButtons button1={goBackButton} button2={submitButton}/>
          </div>

        </Form>
      </div>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: IMtaFormNames.PERSONAL_DETAILS,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector(IMtaFormNames.PERSONAL_DETAILS);
    return component;
  },
  connect(
    (state: IStore, props) => {
      const travellers = selector(state, 'travellers');
      return {
        travellers,
        auth: state.auth,
        activePolicies: state.auth.customer.activePolicies,
        customerMTAOptions: state.branding.channel.products[0].metadata.direct_customer_mta_options,
        formValues: getFormValues(IMtaFormNames.PERSONAL_DETAILS)(state),
      };
    },
  ),
])(PersonalDetailsForm);
